import { mergeAttributes, Node } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from "@tiptap/react";
import { Pause, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAttachmentUrl } from "@/lib/attachments";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    sebasAudio: {
      insertSebasAudio: (attrs: { attachmentId: string; durationMs?: number }) => ReturnType;
    };
  }
}

const fmt = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function AudioView({ node }: NodeViewProps) {
  const attachmentId = node.attrs.attachmentId as string;
  const { url } = useAttachmentUrl(attachmentId);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState((node.attrs.durationMs as number | null) ?? 0 / 1000);

  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const onTime = () => setTime(el.currentTime);
    const onMeta = () => Number.isFinite(el.duration) && setDuration(el.duration);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
    };
  }, [url]);

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    if (playing) el.pause();
    else void el.play();
    setPlaying(!playing);
  };

  const pct = duration > 0 ? (time / duration) * 100 : 0;

  return (
    <NodeViewWrapper className="my-4">
      <div className="flex items-center gap-3 rounded-2xl bg-sunken px-4 py-3" contentEditable={false}>
        <button
          type="button"
          onClick={toggle}
          disabled={!url}
          aria-label={playing ? "Pause" : "Play"}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-accent text-accent-ink disabled:opacity-40"
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-px" />}
        </button>

        <div className="flex-1">
          <div className="h-1.5 overflow-hidden rounded-full bg-line">
            <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1.5 text-xs text-ink-faint">
            {url ? `${fmt(time)} / ${fmt(duration)}` : "Loading audio…"}
          </p>
        </div>

        {url ? <audio ref={audio} src={url} preload="metadata" /> : null}
      </div>
    </NodeViewWrapper>
  );
}

export const SebasAudio = Node.create({
  name: "audio",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes: () => ({
    attachmentId: { default: null },
    durationMs: { default: null },
    /** Populated later by transcription; flattenDoc already indexes it. */
    transcript: { default: null },
  }),

  parseHTML: () => [{ tag: "div[data-sebas-audio]" }],
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-sebas-audio": "" })],
  addNodeView: () => ReactNodeViewRenderer(AudioView),

  addCommands() {
    return {
      insertSebasAudio:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({ type: "audio", attrs }),
    };
  },
});
